// DDF Sync Service - Handles syncing DDF listings to Firestore

import { 
  doc, 
  setDoc, 
  getDoc,
  Timestamp,
  GeoPoint
} from 'firebase/firestore';
import { db } from './firebase';
import { Property } from './realtorApiService';
import { BusinessListing } from './businessService';
import { upsertFranchiseListing, removeFranchiseListing } from './franchiseService';

/**
 * Heuristic: treat DDF listing as franchise if "franchise" appears in
 * PublicRemarks, BusinessType, or PropertySubType (case-insensitive).
 */
export function isFranchiseDDF(ddfProperty: Property): boolean {
  const term = 'franchise';
  const remarks = (ddfProperty.PublicRemarks || '').toLowerCase();
  const subType = (ddfProperty.PropertySubType || '').toLowerCase();
  const types = (ddfProperty.BusinessType || []).map((t) => (t || '').toLowerCase());
  return (
    remarks.includes(term) ||
    subType.includes(term) ||
    types.some((t) => t.includes(term))
  );
}

/**
 * Map DDF Property to BusinessListing format
 */
export function mapDDFPropertyToBusinessListing(ddfProperty: Property): Omit<BusinessListing, 'id'> {
  const now = Timestamp.now();
  
  // Extract images from Media array
  // Accept any media with 'Photo' in the category or type
  const images = ddfProperty.Media
    ?.filter(m => {
      const hasPhotoInCategory = m.MediaCategory?.toLowerCase().includes('photo');
      const hasPhotoInType = m.MediaType?.toLowerCase().includes('photo');
      const hasImageInType = m.MediaType?.toLowerCase().includes('image');
      return (hasPhotoInCategory || hasPhotoInType || hasImageInType) && m.MediaURL;
    })
    .sort((a, b) => (a.MediaOrder || 0) - (b.MediaOrder || 0))
    .map(m => m.MediaURL || '')
    .filter(url => url) || [];

  // Build address
  const addressParts = [
    ddfProperty.StreetNumber,
    ddfProperty.StreetDirPrefix,
    ddfProperty.StreetName,
    ddfProperty.StreetSuffix,
    ddfProperty.StreetDirSuffix,
    ddfProperty.UnitNumber
  ].filter(Boolean);
  const address = addressParts.join(' ').trim() || ddfProperty.UnparsedAddress || '';

  // Determine price range
  const price = ddfProperty.ListPrice || 0;
  let priceRange: 'under_100k' | '100k_500k' | '500k_1m' | '1m_5m' | 'over_5m' = 'under_100k';
  if (price >= 100000 && price < 500000) priceRange = '100k_500k';
  else if (price >= 500000 && price < 1000000) priceRange = '500k_1m';
  else if (price >= 1000000 && price < 5000000) priceRange = '1m_5m';
  else if (price >= 5000000) priceRange = 'over_5m';

  // Map StandardStatus to our status
  let status: 'active' | 'pending' | 'sold' | 'expired' | 'draft' = 'active';
  const ddfStatus = ddfProperty.StandardStatus?.toLowerCase() || '';
  if (ddfStatus.includes('sold') || ddfStatus.includes('closed')) status = 'sold';
  else if (ddfStatus.includes('pending') || ddfStatus.includes('hold')) status = 'pending';
  else if (ddfStatus.includes('expired') || ddfStatus.includes('cancelled')) status = 'expired';
  else if (ddfStatus.includes('active') || ddfStatus.includes('available')) status = 'active';

  // Create coordinates
  let coordinates: GeoPoint;
  if (ddfProperty.Latitude && ddfProperty.Longitude) {
    coordinates = new GeoPoint(ddfProperty.Latitude, ddfProperty.Longitude);
  } else {
    // Default to Toronto if no coordinates
    coordinates = new GeoPoint(43.6532, -79.3832);
  }

  // Build searchable text
  const searchableText = [
    ddfProperty.PublicRemarks,
    ddfProperty.City,
    ddfProperty.StateOrProvince,
    ddfProperty.SubdivisionName,
    ddfProperty.BusinessType?.join(' '),
    ddfProperty.PropertySubType
  ].filter(Boolean).join(' ').toLowerCase();

  // Map business type to category
  const businessType = ddfProperty.BusinessType?.[0] || 'Other';
  const category = mapBusinessTypeToCategory(businessType);

  // Calculate financial metrics (estimates for DDF listings)
  const askingPrice = price;
  const annualRevenue = askingPrice * 0.3; // Estimate 30% of asking price as revenue
  const annualExpenses = annualRevenue * 0.6; // Estimate 60% expenses
  const annualCashFlow = annualRevenue - annualExpenses;
  const profitMargin = annualRevenue > 0 ? (annualCashFlow / annualRevenue) * 100 : 0;

  const businessListing: Omit<BusinessListing, 'id'> = {
    basic: {
      title: ddfProperty.PublicRemarks?.substring(0, 100) || `${businessType} Business for Sale`,
      description: ddfProperty.PublicRemarks || 'Business listing from REALTOR.ca',
      shortDescription: ddfProperty.PublicRemarks?.substring(0, 200) || '',
      category: category,
      subcategory: businessType,
      tags: ddfProperty.BusinessType || [],
      status: status,
      featured: false,
      verified: true, // DDF listings are verified
      createdAt: ddfProperty.OriginalEntryTimestamp 
        ? Timestamp.fromDate(new Date(ddfProperty.OriginalEntryTimestamp))
        : now,
      updatedAt: ddfProperty.ModificationTimestamp
        ? Timestamp.fromDate(new Date(ddfProperty.ModificationTimestamp))
        : now,
      expiresAt: new Timestamp(now.seconds + (365 * 24 * 60 * 60), 0), // 1 year
      publishedAt: ddfProperty.OriginalEntryTimestamp
        ? Timestamp.fromDate(new Date(ddfProperty.OriginalEntryTimestamp))
        : now
    },
    location: {
      address: address,
      city: ddfProperty.City || '',
      province: ddfProperty.StateOrProvince || '',
      postalCode: ddfProperty.PostalCode || '',
      country: ddfProperty.Country || 'Canada',
      coordinates: coordinates,
      region: ddfProperty.CityRegion || ddfProperty.SubdivisionName || '',
      timezone: 'America/Toronto' // Default, could be improved
    },
    financial: {
      askingPrice: askingPrice,
      currency: 'CAD',
      annualRevenue: annualRevenue,
      annualCashFlow: annualCashFlow,
      annualExpenses: annualExpenses,
      profitMargin: profitMargin,
      growthRate: 0,
      pricePerRevenue: annualRevenue > 0 ? askingPrice / annualRevenue : 0,
      pricePerCashFlow: annualCashFlow > 0 ? askingPrice / annualCashFlow : 0,
      financingAvailable: false
    },
    business: {
      established: ddfProperty.YearBuilt || new Date().getFullYear() - 5,
      employees: {
        min: 1,
        max: 10
      },
      squareFootage: ddfProperty.BuildingAreaTotal || 0,
      leaseTerms: ddfProperty.LeaseAmountFrequency || 'Monthly',
      reasonForSale: 'Business opportunity',
      trainingIncluded: false,
      relocatable: false,
      workFromHome: false,
      absenteeOwnership: false,
      franchise: isFranchiseDDF(ddfProperty)
    },
    media: {
      images: images,
      videos: ddfProperty.Media?.filter(m => m.MediaType === 'Virtual Tour').map(m => m.MediaURL || '').filter(Boolean) || [],
      documents: ddfProperty.Media?.filter(m => m.MediaCategory === 'Floor Plan').map(m => m.MediaURL || '').filter(Boolean) || [],
      featuredImage: images[0] || ''
    },
    owner: {
      userId: 'ddf-system', // System user for DDF listings
      brokerId: ddfProperty.ListAgentKey || undefined,
      contactInfo: {
        name: 'REALTOR®',
        email: '',
        phone: '',
        preferredContact: 'email' as const
      },
      responseTime: 24
    },
    search: {
      searchableText: searchableText,
      priceRange: priceRange,
      revenueRange: priceRange, // Same as price range for simplicity
      locationKeywords: [
        ddfProperty.City,
        ddfProperty.StateOrProvince,
        ddfProperty.SubdivisionName,
        ddfProperty.CityRegion
      ].filter((keyword): keyword is string => keyword !== null && keyword !== undefined),
      categoryKeywords: ddfProperty.BusinessType?.filter((type): type is string => type !== null && type !== undefined) || []
    }
  };

  return businessListing;
}

/**
 * Map DDF BusinessType to our category.
 * Franchise is a classification (business.franchise), not a category — we never return "Franchises".
 * Types like "Franchise" or "Franchise resales" fall through to "Other".
 */
function mapBusinessTypeToCategory(businessType: string): string {
  const type = businessType.toLowerCase();
  if (type.includes('restaurant') || type.includes('food')) return 'Restaurant & Food';
  if (type.includes('retail') || type.includes('store')) return 'Retail';
  if (type.includes('service')) return 'Services';
  if (type.includes('manufacturing') || type.includes('production')) return 'Manufacturing';
  if (type.includes('automotive') || type.includes('auto')) return 'Automotive';
  if (type.includes('health') || type.includes('medical')) return 'Health & Beauty';
  if (type.includes('fitness') || type.includes('gym')) return 'Fitness & Recreation';
  if (type.includes('hotel') || type.includes('lodging')) return 'Hospitality';
  if (type.includes('agriculture') || type.includes('farming')) return 'Agriculture';
  return 'Other';
}

/**
 * Save DDF listing to Firestore
 */
export async function saveDDFListingToFirestore(
  ddfProperty: Property
): Promise<string> {
  try {
    const listingKey = ddfProperty.ListingKey;
    if (!listingKey) {
      throw new Error('ListingKey is required');
    }

    // Map to BusinessListing format
    const businessListing = mapDDFPropertyToBusinessListing(ddfProperty);

    // Add source metadata
    const listingWithMetadata = {
      ...businessListing,
      source: 'ddf',
      ddfData: {
        ListingKey: ddfProperty.ListingKey,
        ListingId: ddfProperty.ListingId,
        ListingURL: ddfProperty.ListingURL,
        ModificationTimestamp: ddfProperty.ModificationTimestamp,
        OriginalEntryTimestamp: ddfProperty.OriginalEntryTimestamp,
        StandardStatus: ddfProperty.StandardStatus,
        ListAgentKey: ddfProperty.ListAgentKey,
        ListOfficeKey: ddfProperty.ListOfficeKey,
        // Extract agent/office names from property data (may exist but not in TypeScript interface)
        ListAgentName: (ddfProperty as any).ListAgentName || 
                       ((ddfProperty as any).ListAgent?.MemberFirstName && (ddfProperty as any).ListAgent?.MemberLastName
                         ? `${(ddfProperty as any).ListAgent.MemberFirstName} ${(ddfProperty as any).ListAgent.MemberLastName}`.trim()
                         : null) ||
                       ((ddfProperty as any).ListAgent?.MemberName
                         ? (ddfProperty as any).ListAgent.MemberName
                         : null) ||
                       null,
        ListOfficeName: (ddfProperty as any).ListOfficeName || 
                        ((ddfProperty as any).ListOffice?.OfficeName
                          ? (ddfProperty as any).ListOffice.OfficeName
                          : null) ||
                        null
      },
      syncMetadata: {
        lastSynced: Timestamp.now(),
        syncedAt: Timestamp.now()
      }
    };

    // Save to ddfListings collection using ListingKey as document ID
    const docRef = doc(db, 'ddfListings', listingKey);
    await setDoc(docRef, listingWithMetadata);

    // Sync franchise collection when DDF listing is detected as franchise
    try {
      if (isFranchiseDDF(ddfProperty)) {
        await upsertFranchiseListing('ddfListings', listingKey, {
          title: businessListing.basic.title,
          description: businessListing.basic.description,
          category: businessListing.basic.category,
          subcategory: businessListing.basic.subcategory,
          province: businessListing.location.province,
          city: businessListing.location.city,
          askingPrice: businessListing.financial.askingPrice,
          currency: businessListing.financial.currency,
          annualRevenue: businessListing.financial.annualRevenue,
          annualCashFlow: businessListing.financial.annualCashFlow,
          featuredImage: businessListing.media.featuredImage || businessListing.media.images?.[0] || '',
          status: businessListing.basic.status,
          createdAt: businessListing.basic.createdAt,
          updatedAt: businessListing.basic.updatedAt,
          tags: businessListing.basic.tags,
          seoUrl: businessListing.basic.seoUrl,
        });
      } else {
        await removeFranchiseListing('ddfListings', listingKey);
      }
    } catch (franchiseErr) {
      console.warn('Franchise collection sync failed for DDF listing (non-blocking):', franchiseErr);
    }

    return listingKey;
  } catch (error) {
    console.error('Error saving DDF listing to Firestore:', error);
    throw error;
  }
}

/**
 * Check if a DDF listing already exists in Firestore
 */
export async function ddfListingExists(listingKey: string): Promise<boolean> {
  try {
    const docRef = doc(db, 'ddfListings', listingKey);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  } catch (error) {
    console.error('Error checking if listing exists:', error);
    return false;
  }
}
